import PropTypes from 'prop-types';
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';
import ThemeProvider from './index';

// Colour mode
// ----------------------------------------------------------------------
// Operators sit in this tool all day, so the choice belongs to them and has to
// survive a reload. It is stored the same way language is — a single key in
// localStorage — rather than on the user record, since it is a property of the
// machine you are sitting at, not of the account.

export const COLOR_MODE_STORAGE_KEY = 'colorMode';

const isMode = (value) => value === 'light' || value === 'dark';

const systemPrefersDark = () =>
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;

/** The stored preference, or null when the user has never chosen one. */
export const readStoredColorMode = () => {
    try {
        const stored = localStorage.getItem(COLOR_MODE_STORAGE_KEY);
        return isMode(stored) ? stored : null;
    } catch (e) {
        // Private-mode Safari throws on localStorage access.
        return null;
    }
};

export const readColorMode = () =>
    readStoredColorMode() || (systemPrefersDark() ? 'dark' : 'light');

const writeColorMode = (mode) => {
    try {
        localStorage.setItem(COLOR_MODE_STORAGE_KEY, mode);
    } catch (e) {
        // The preference just won't survive a reload.
    }
};

const ColorModeContext = createContext({ mode: 'light', toggle: () => {} });

/** `{ mode, toggle }` for whatever wants to render the switch. */
export const useColorMode = () => useContext(ColorModeContext);

export function ColorModeProvider({ children }) {
    const [mode, setMode] = useState(readColorMode);

    const toggle = useCallback(() => {
        setMode((current) => {
            const next = current === 'dark' ? 'light' : 'dark';
            writeColorMode(next);
            return next;
        });
    }, []);

    // Follow the OS only until the user states a preference of their own —
    // after that, an explicit choice outranks the system setting.
    useEffect(() => {
        if (readStoredColorMode()) return undefined;
        if (typeof window.matchMedia !== 'function') return undefined;

        const query = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = (event) => {
            if (!readStoredColorMode()) {
                setMode(event.matches ? 'dark' : 'light');
            }
        };

        query.addEventListener('change', onChange);
        return () => query.removeEventListener('change', onChange);
    }, []);

    const value = useMemo(() => ({ mode, toggle }), [mode, toggle]);

    return (
        <ColorModeContext.Provider value={value}>
            <ThemeProvider mode={mode}>{children}</ThemeProvider>
        </ColorModeContext.Provider>
    );
}

ColorModeProvider.propTypes = {
    children: PropTypes.node
};

export default ColorModeProvider;
